/**
 * Ручки уведомлений. Обе уже есть на бэкенде и ничего дописывать не пришлось
 * (`src/modules/notice/rules.php`):
 *
 *   GET   /api/notices            → список + счётчик непрочитанных
 *   PATCH /api/notices/{id}/read  → отметка «прочитано»
 *
 * Отметка уходит на сервер (строка в `wp_notice_read`), поэтому переживает
 * перезагрузку страницы. Список содержит и прочитанные (`new: 0`) — чтобы
 * прочитанное можно было открыть ещё раз.
 */
import DOMPurify from 'dompurify'

import { httpClient } from '@/shared/api/httpClient'
import type {
  NotificationsPayload,
  NotificationsResponse,
  NotificationRaw,
} from '../types/notifications'

/** Максимальная длина превью в списке — дальше многоточие. */
const PREVIEW_LENGTH = 140

export function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function toPreview(value: string, maxLength = PREVIEW_LENGTH): string {
  const plain = stripHtml(value)

  return plain.length > maxLength ? `${plain.slice(0, maxLength).trimEnd()}…` : plain
}

/**
 * Тело уведомления приходит как HTML и попадает в `v-html`, поэтому чистится
 * здесь — до того, как его увидит любой компонент. `target` в списке
 * разрешённых атрибутов: в текстах встречаются ссылки, открывающиеся в новой
 * вкладке.
 */
export function sanitizeNotificationHtml(value: string): string {
  return linkifyHtml(DOMPurify.sanitize(value, {
    ADD_ATTR: ['target'],
    RETURN_TRUSTED_TYPE: false,
  }) as unknown as string)
}

/** Адрес в тексте; хвостовую пунктуацию предложения в ссылку не берём. */
const URL_PATTERN = /https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)»]/g

/**
 * Делает кликабельными голые адреса в тексте (бэкенд присылает ссылку на ключ
 * просто строкой) и открывает все внешние ссылки в новой вкладке, чтобы
 * кабинет не терялся. Работает по уже обеззараженному HTML.
 */
export function linkifyHtml(html: string): string {
  const template = document.createElement('template')
  template.innerHTML = html

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    if (!node.parentElement?.closest('a')) textNodes.push(node)
  }

  for (const node of textNodes) {
    const text = node.nodeValue ?? ''
    const matches = [...text.matchAll(URL_PATTERN)]
    if (matches.length === 0) continue

    const fragment = document.createDocumentFragment()
    let last = 0
    for (const match of matches) {
      const start = match.index ?? 0
      const link = document.createElement('a')
      link.href = match[0]
      link.textContent = match[0]
      fragment.append(text.slice(last, start), link)
      last = start + match[0].length
    }
    fragment.append(text.slice(last))
    node.replaceWith(fragment)
  }

  template.content.querySelectorAll<HTMLAnchorElement>('a[href^="http"]').forEach((link) => {
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
  })

  return template.innerHTML
}

/**
 * Разбирает ответ списка.
 *
 * Счётчик берём у сервера через `??`, а не `||`: честный `new_pm_cnt: 0`
 * не должен проваливаться в запасной подсчёт. Запасной подсчёт нужен только
 * если поля вообще нет.
 */
export function parseNotificationsResponse(response: NotificationsResponse): NotificationsPayload {
  if (response?.result !== 'success') {
    throw new Error('NOTIFICATIONS_LOAD_FAILED')
  }

  const data = response.data
  // Гостю бэкенд отвечает пустым массивом вместо объекта — это не список.
  const bag = data && !Array.isArray(data) ? data : {}

  const items: NotificationRaw[] = Array.isArray(bag.notifications) ? bag.notifications : []
  const unreadCount =
    typeof bag.new_pm_cnt === 'number'
      ? bag.new_pm_cnt
      : items.filter((item) => item?.new === 1).length

  return { items, unreadCount }
}

export async function fetchNotifications(): Promise<NotificationsPayload> {
  const response = await httpClient.get<NotificationsResponse>('/notices')

  return parseNotificationsResponse(response)
}

export async function markNotificationRead(id: string): Promise<void> {
  await httpClient.patch(`/notices/${encodeURIComponent(id)}/read`)
}
